/**
 * OfflineBanner — shown at the top of the app when the device is offline
 * or when there are pending writes waiting to sync.
 *
 * States:
 *   offline + writes   → amber "You're offline — N changes will sync when reconnected"
 *   offline, no writes → amber "You're offline — viewing cached data"
 *   online + syncing   → brief blue "Syncing…" (auto-hides after 3 s)
 *   online, no pending → hidden
 */

import { useNetworkStatus } from '../lib/useNetworkStatus'

export default function OfflineBanner() {
  const { isOnline, pendingWrites, isSyncing } = useNetworkStatus()

  if (isOnline && !isSyncing && pendingWrites === 0) return null

  if (!isOnline) {
    return (
      <div
        role="status"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: 'var(--color-warning, #d97706)',
          color: '#fff',
          padding: '8px 16px',
          fontSize: 13,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <span>⚠</span>
        {pendingWrites > 0
          ? `You're offline — ${pendingWrites} change${pendingWrites === 1 ? '' : 's'} will sync when reconnected`
          : "You're offline — viewing cached data"}
      </div>
    )
  }

  if (isSyncing) {
    return (
      <div
        role="status"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          background: 'var(--brand-primary, #2563eb)',
          color: '#fff',
          padding: '6px 16px',
          fontSize: 13,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
        Syncing…
      </div>
    )
  }

  // Pending writes but online (outbox not yet flushed)
  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: 'var(--color-warning, #d97706)',
        color: '#fff',
        padding: '6px 16px',
        fontSize: 13,
        textAlign: 'center',
      }}
    >
      {pendingWrites} pending change{pendingWrites === 1 ? '' : 's'} — syncing…
    </div>
  )
}
